import { skyFlightCapabilities } from './106-sky-capabilities.mjs';
import {
  createSkyCathedral,
  stepSkyCathedral,
  skyCathedralSystems,
  applySkyCathedralContact,
  SKY_CATHEDRAL_POLICY,
  failSkyCathedralPilot,
} from './101-sky-cathedral.mjs';
import {
  SKY_CATHEDRAL_SPACE as SPACE,
  skyCathedralCity,
  skyCathedralSweep,
  skyCathedralMovingSweep,
  skyCathedralResolvePilot,
} from './99-sky-cathedral-space.mjs';
import { skyFlightFrameFromTangent, skyFlightVectorToWorld } from './92-sky-flight-frame.mjs';
import { SKY_FLIGHT_AIM } from './86-sky-world-scale.mjs';
import { skyLeviathanTriangleSweep } from './96-sky-leviathan-contacts.mjs';
import { skyEquipment, SKY_UPGRADE_PATHS } from './77-sky-upgrades.mjs';
import {
  skyEffectiveEquipment,
  skyAbsorbBonusHit,
  stepSkyBonuses,
  clearSkyBonuses,
} from './82-sky-bonuses.mjs';
import { stepSkyOverdrive } from './79-sky-precision.mjs';
import { skyValidatedAimPoint, skyProjectileContact } from './80-sky-aim.mjs';
import { emitSkyShots, stepSkyProjectiles, skyProjectileAlive } from './81-sky-projectiles.mjs';

const p = (x, y, z) => ({ x, y, z });
const finite = (v) => v && ['x', 'y', 'z'].every((axis) => Number.isFinite(v[axis]));
const copy = (v) => p(v.x, v.y, v.z);
const position = (s) => p(s.x, s.y, s.pilotZ);
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const maximumHp =
  SPACE.legIds.length * SKY_CATHEDRAL_POLICY.kneeHp + SKY_CATHEDRAL_POLICY.generatorHp;
const identity = skyFlightFrameFromTangent(p(0, 0, 0), p(0, 0, -1));

function validArrival(e) {
  return (
    e?.pose?.valid &&
    Math.abs(e.pilot.x) <= 55 &&
    Math.abs(e.pilot.z) <= 65 &&
    e.pilot.y <= 40 &&
    e.pilot.y >= SPACE.waterY + SPACE.pilotRadius + 0.1 &&
    !skyCathedralSweep(e.pilot, e.pilot, e.pose, SPACE.pilotRadius)
  );
}

/** Prepare the authored arrival before controls open. Campaign clocks, lives,
 * earned equipment and rewards remain caller-owned. Repeated same-sector
 * installation preserves progress; invalid arrival geometry changes nothing. */
export function startSkyCathedralFlight(s, { pilot, requireArtwork = false } = {}) {
  if (!s || !Number.isInteger(s.sector)) return null;
  if (s.cathedral?.sector === s.sector)
    return !requireArtwork || s.cathedral.pose?.artwork ? s.cathedral : null;
  const e = createSkyCathedral({ pilot, geometry: s.cathedralCollision });
  if (!validArrival(e) || (requireArtwork && !e.pose.artwork)) return null;
  Object.assign(e, {
    sector: s.sector,
    encounterId: 'cathedral-of-thunder',
    componentIds: {},
    consumedEvent: 0,
    settled: false,
  });
  Object.assign(s, {
    cathedral: e,
    x: e.pilot.x,
    y: e.pilot.y,
    pilotZ: e.pilot.z,
    freeFlight: true,
    locomotion: 'fly',
    flightFrame: null,
    flightAimDepth: SKY_FLIGHT_AIM.depth,
    aimPoint: null,
    aimTarget: null,
    aimFrom: null,
    aimReturnAt: null,
    traversal: null,
    enemies: [],
    shots: [],
    threats: [],
    fences: [],
    motionHold: null,
    boss: {
      id: s.nextId++,
      kind: 'boss',
      collisionEnabled: false,
      hp: 1,
      maxHp: maximumHp,
      name: 'CATHEDRAL OF THUNDER',
      artVariant: 'cathedral-of-thunder',
      x: 0,
      y: 0,
      z: 0,
      encounterSector: s.sector,
      controlMode: 'flyby',
    },
  });
  refresh(s);
  return e;
}

/** Standalone mechanical blockout, deliberately outside75/76 campaign policy.
 * Start with input.start; real axis/target steering is speed bounded and all
 * accepted damage is emitted/resolved by the existing physical projectile code.
 * Invalid optional equipment resolves to the same zero-tier policy as77. */
export function createSkyCathedralRun({ upgrades = {}, pilot, geometry = null } = {}) {
  const e = createSkyCathedral({ pilot, geometry });
  if (!validArrival(e)) return null;
  const levels = Object.fromEntries(
    Object.entries(SKY_UPGRADE_PATHS).map(([key, path]) => [
      key,
      Number.isInteger(upgrades?.[key]) && upgrades[key] >= 0 && upgrades[key] <= path.maxLevel
        ? upgrades[key]
        : 0,
    ]),
  );
  const s = {
    status: 'ready',
    cathedralCollision: geometry,
    sector: 0,
    time: 0,
    tick: 0,
    x: e.pilot.x,
    y: e.pilot.y,
    pilotZ: e.pilot.z,
    cathedralPose: e.pose,
    upgrades: levels,
    bonuses: {},
    pulse: 1,
    pulseTime: 0,
    lives: skyEquipment(levels).maxLives,
    locomotion: 'fly',
    flightFrame: null,
    flightAimDepth: SKY_FLIGHT_AIM.depth,
    aimPoint: null,
    traversal: null,
    score: 0,
    nextId: 1,
    shot: 0,
    shots: [],
    enemies: [],
    threats: [],
    bursts: [],
    drones: [],
    shotsFired: 0,
    hitsLanded: 0,
    criticalHits: 0,
    armorHits: 0,
    droneShotsFired: 0,
    overdriveTime: 0,
    overdriveRecovery: 0,
    overdriveCharge: 0,
    damageTaken: 0,
    grace: 0,
    fences: [],
    boss: { kind: 'boss', collisionEnabled: false, hp: 1, maxHp: 1, controlMode: 'flyby' },
  };
  startSkyCathedralFlight(s, { pilot });
  return s;
}

function identifier(s, key) {
  return (s.cathedral.componentIds[key] ??= s.nextId++);
}
/** Select a real unbroken conductor for readable guidance. A four-metre
 * hysteresis prevents left/right flicker at the centre line. This changes the
 * shared view/control bearing only; it never moves the pilot or aims a shot. */
function groundingGuidance(s, systems) {
  const e = s.cathedral;
  if (!['arrival', 'grounding'].includes(e.phase)) return null;
  const ready = Object.keys(e.legs).filter((id) => !e.legs[id].broken);
  ready.sort(
    (a, b) =>
      distance(position(s), e.pose.legs[a].baitPocket) -
      distance(position(s), e.pose.legs[b].baitPocket),
  );
  let id = systems.aimedLeg ?? ready[0];
  const previous = s.cathedralGuidance?.legId;
  if (
    !systems.aimedLeg &&
    ready.includes(previous) &&
    distance(position(s), e.pose.legs[previous].baitPocket) <
      distance(position(s), e.pose.legs[id].baitPocket) + 4
  )
    id = previous;
  if (!id) return null;
  const locked = systems.aimedLeg === id,
    leg = e.pose.legs[id];
  return {
    legId: id,
    kind: locked ? 'knee' : 'rod',
    point: copy(locked ? leg.target.center : leg.catchpoint.center),
    partId: locked ? leg.target.partId : leg.catchpoint.partId,
    ready: !locked || systems.openLegs.includes(id),
  };
}

function refresh(s) {
  const e = s.cathedral,
    pose = e.pose,
    systems = skyCathedralSystems(e);
  s.cathedralPose = pose;
  s.cathedralGuidance = groundingGuidance(s, systems);
  let focus = systems.aimedLeg
    ? pose.legs[systems.aimedLeg].target.center
    : s.cathedralGuidance
      ? s.cathedralGuidance.point
      : e.phase === 'nave'
        ? (pose.generator.target ?? pose.generator).center
        : p(pose.chassis.origin.x, pose.chassis.origin.y + 10, pose.chassis.origin.z);
  let up = e.phase === 'nave' ? pose.chassis.up : p(0, 1, 0);
  // Acquire the nave's target and tilted up vector before protection ends.
  // Changing both on the first actionable frame would abruptly rotate sight.
  if (e.phase === 'kneel') {
    const progress = clamp((e.phaseAge - SKY_CATHEDRAL_POLICY.kneelSeconds + 0.6) / 0.6, 0, 1);
    const t = progress * progress * (3 - 2 * progress);
    const target = (pose.generator.target ?? pose.generator).center;
    focus = p(
      focus.x + (target.x - focus.x) * t,
      focus.y + (target.y - focus.y) * t,
      focus.z + (target.z - focus.z) * t,
    );
    up = p(pose.chassis.up.x * t, 1 + (pose.chassis.up.y - 1) * t, pose.chassis.up.z * t);
  }
  let dir = p(focus.x - s.x, focus.y - s.y, focus.z - s.pilotZ);
  s.viewTarget = copy(focus);
  if (systems.aimedLeg !== s.aimTarget) {
    s.aimReturnAt = systems.aimedLeg ? null : s.time;
    s.aimTarget = systems.aimedLeg;
    s.aimFrom = s.flightFrame
      ? p(-s.flightFrame.back.x, -s.flightFrame.back.y, -s.flightFrame.back.z)
      : null;
  }
  const returning = s.aimReturnAt != null && s.time - s.aimReturnAt < 0.6;
  if ((systems.aimPreparing || returning) && s.aimFrom) {
    const length = Math.hypot(dir.x, dir.y, dir.z) || 1;
    const progress = returning
      ? clamp((s.time - s.aimReturnAt) / 0.6, 0, 1)
      : systems.aimPreparation;
    const t = progress ** 2 * (3 - 2 * progress);
    dir = p(
      s.aimFrom.x * (1 - t) + (dir.x / length) * t,
      s.aimFrom.y * (1 - t) + (dir.y / length) * t,
      s.aimFrom.z * (1 - t) + (dir.z / length) * t,
    );
    s.viewTarget = p(s.x + dir.x * length, s.y + dir.y * length, s.pilotZ + dir.z * length);
  }
  if (Math.hypot(dir.x, dir.y, dir.z) > 0.01) {
    // up × back preserves upright flight when circling a leg; transporting
    // an arbitrary previous right vector can slowly roll a sideways view.
    const right = p(
      -up.y * dir.z + up.z * dir.y,
      -up.z * dir.x + up.x * dir.z,
      -up.x * dir.y + up.y * dir.x,
    );
    s.flightFrame = skyFlightFrameFromTangent(
      position(s),
      dir,
      Math.hypot(right.x, right.y, right.z) > 1e-9 ? right : s.flightFrame?.right,
    );
    const movementUp = e.phase === 'nave' ? pose.chassis.up : p(0, 1, 0);
    const dot = dir.x * movementUp.x + dir.y * movementUp.y + dir.z * movementUp.z;
    const horizontal = p(
      dir.x - movementUp.x * dot,
      dir.y - movementUp.y * dot,
      dir.z - movementUp.z * dot,
    );
    s.controlFrame =
      skyFlightFrameFromTangent(position(s), horizontal, s.flightFrame.right) ?? s.controlFrame;
  }
  const targets = new Map(
    Object.entries(pose.legs).map(([id, leg]) => [
      leg.target.partId,
      { id, target: leg.target, state: e.legs[id] },
    ]),
  );
  const generator = pose.generator.target ?? pose.generator;
  targets.set(generator.partId, { id: 'generator', target: generator, state: e.generator });
  s.enemies = [...skyCathedralCity(pose), ...pose.parts]
    .filter((part) => part.collisionEnabled !== false)
    .map((part) => {
      const component = targets.get(part.id);
      return {
        id: identifier(s, part.id),
        kind: component ? 'boss' : 'pillar',
        hp: Infinity,
        ...part.frame.origin,
        radius: Math.hypot(part.halfExtents.x, part.halfExtents.y, part.halfExtents.z),
        contactShape: 'mesh',
        triangles: part.triangles,
        contactFrame: part.frame,
        cathedralPart: part.id,
        componentId: component?.id ?? null,
        objectiveHp: component?.state.hp ?? null,
        assistPoint: component?.target.center ?? component?.target ?? null,
        vulnerable: component
          ? component.id === 'generator'
            ? systems.generatorOpen
            : systems.openLegs.includes(component.id)
          : false,
      };
    });
  s.boss.controlMode = systems.controls ? 'aim' : 'flyby';
  s.boss.aimWindow = systems.controls && !systems.aimPreparing && !e.motionHeld;
  s.boss.aimPreparing = systems.aimPreparing;
  s.boss.aimPreparation = systems.aimPreparation;
  s.boss.phase = e.phase;
  s.boss.hp = Object.values(e.legs).reduce((sum, leg) => sum + leg.hp, e.generator.hp);
  s.boss.maxHp = maximumHp;
  s.fences = fencePanels(e);
  e.fences = s.fences;
}

function fencePanels(e) {
  if (e.phase !== 'grounding') return [];
  const within = e.phaseAge % 12,
    side = within < 6 ? 'left' : 'right',
    clock = within % 6;
  if (e.legs[`rear-${side}`].broken) return [];
  const a = e.pose.spireSockets[`front-${side}`],
    b = e.pose.spireSockets[`rear-${side}`];
  if (!finite(a) || !finite(b)) return [];
  const c = p(b.x, SPACE.waterY + 2, b.z),
    d = p(a.x, SPACE.waterY + 2, a.z);
  const triangles = [
    { a, b, c },
    { a, b: c, c: d },
  ];
  const requested = clock >= 1.8 && clock < 4.8;
  const alreadyActive = e.fences?.some((panel) => panel.id === side && panel.active);
  const occupied = triangles.some((triangle) =>
    skyLeviathanTriangleSweep(e.pilot, e.pilot, triangle, SPACE.pilotRadius),
  );
  return [
    {
      id: side,
      charge: clamp(clock / 1.8, 0, 1),
      active: requested && (alreadyActive || !occupied),
      formationHeld: requested && !alreadyActive && occupied,
      vertices: [copy(a), copy(b), c, d],
      triangles,
    },
  ];
}

function hurt(s, source, callbacks) {
  if (s.grace > 0 || s.lives <= 0 || !skyCathedralSystems(s.cathedral).controls) return;
  if (callbacks?.hit) return callbacks.hit(s, source);
  if (skyAbsorbBonusHit(s)) return;
  s.lives--;
  s.damageTaken++;
  s.grace = skyEquipment(s.upgrades).shieldGrace;
  s.lastDamage = { source, time: s.time, ...position(s) };
  s.bursts.push({ id: s.nextId++, ...position(s), effect: 'damage', life: 0.6 });
}
function move(s, input, dt, callbacks, worldSteering) {
  const from = position(s),
    speed = Math.min(SKY_CATHEDRAL_POLICY.maximumPilotSpeed, skyEffectiveEquipment(s).lateralSpeed);
  const to = copy(from);
  const bounds = { x: [-55, 55], y: [SPACE.waterY + SPACE.pilotRadius + 0.1, 40], z: [-65, 65] };
  const deltas = {};
  const axes = skyFlightVectorToWorld(
    s.controlFrame,
    p(
      Number.isFinite(input.x) ? clamp(input.x, -1, 1) : 0,
      Number.isFinite(input.y) ? clamp(input.y, -1, 1) : 0,
      Number.isFinite(input.forward) ? -clamp(input.forward, -1, 1) : 0,
    ),
  );
  for (const axis of ['x', 'y', 'z']) {
    const target = worldSteering ? input[`target${axis.toUpperCase()}`] : undefined;
    const delta = Number.isFinite(target)
      ? target - from[axis]
      : worldSteering
        ? Number.isFinite(input[axis])
          ? clamp(input[axis], -1, 1) * speed * dt
          : 0
        : axes
          ? axes[axis] * speed * dt
          : 0;
    deltas[axis] = clamp(delta, -speed * dt, speed * dt);
  }
  const length = Math.hypot(deltas.x, deltas.y, deltas.z),
    scale = length > speed * dt ? (speed * dt) / length : 1;
  for (const axis of ['x', 'y', 'z'])
    to[axis] = clamp(from[axis] + deltas[axis] * scale, ...bounds[axis]);
  const hit = skyCathedralSweep(from, to, s.cathedral.pose, SPACE.pilotRadius);
  if (hit) {
    const fraction = Math.max(0, hit.fraction - 1e-5);
    for (const axis of ['x', 'y', 'z']) to[axis] = from[axis] + (to[axis] - from[axis]) * fraction;
    // Try tangential components separately, never teleport through a corner.
    for (const axis of ['x', 'y', 'z']) {
      const slide = { ...to, [axis]: clamp(from[axis] + deltas[axis] * scale, ...bounds[axis]) };
      if (!skyCathedralSweep(to, slide, s.cathedral.pose, SPACE.pilotRadius))
        Object.assign(to, slide);
    }
    if (distance(from, to) < length * 0.5) hurt(s, 'collision', callbacks);
  }
  s.x = to.x;
  s.y = to.y;
  s.pilotZ = to.z;
  s.vx = (to.x - from.x) / dt;
  s.vy = (to.y - from.y) / dt;
  s.vz = (to.z - from.z) / dt;
  return from;
}
function hazards(s, previous, callbacks) {
  const strike = s.cathedral.strike;
  if (strike?.stage === 'active') {
    const hit = skyProjectileContact(
      { kind: 'pillar', contactShape: 'sphere', ...position(s), radius: SPACE.pilotRadius },
      strike.origin,
      strike.target,
      identity,
    );
    if (hit) hurt(s, 'lightning', callbacks);
  }
  for (const panel of s.fences)
    if (
      panel.active &&
      panel.triangles.some((triangle) =>
        skyLeviathanTriangleSweep(previous, position(s), triangle, SPACE.pilotRadius),
      )
    )
      hurt(s, 'fence', callbacks);
}
function settle(s, callbacks) {
  const e = s.cathedral;
  for (const event of s.cathedral.events) {
    if (event.id <= e.consumedEvent) continue;
    e.consumedEvent = event.id;
    if (event.kind === 'component-disabled') {
      const scoreAward = event.component === 'generator' ? 1000 : 250;
      s.score += scoreAward;
      callbacks?.earnSalvage?.(s, 1);
      if (callbacks?.burst) callbacks.burst(s, event.point, 'gold', 'kill', { scoreAward });
      else s.bursts.push({ id: s.nextId++, ...event.point, scoreAward, life: 1, effect: 'kill' });
    } else if (event.kind === 'phase-entered') {
      callbacks?.event?.(s, 'boss-phase', `CATHEDRAL · ${event.phase.toUpperCase()}`);
    }
  }
  if (s.cathedral.phase === 'complete' && !e.settled) {
    e.settled = true;
    s.score += 3000;
    if (callbacks?.clearStage) {
      s.guardiansDefeated++;
      callbacks.earnSalvage?.(s, 4);
      Object.assign(s, {
        flightFrame: null,
        controlFrame: null,
        flightAimDepth: null,
        freeFlight: false,
        aimPoint: null,
        boss: null,
        enemies: [],
        shots: [],
        threats: [],
        fences: [],
      });
      callbacks.clearStage(s);
      s.cathedral = null;
      s.cathedralPose = null;
    } else s.status = 'complete';
  } else if (s.cathedral.phase === 'lost') {
    s.status = 'over';
    clearSkyBonuses(s);
  }
}

function mechanismSnapshot(e) {
  return {
    ...e,
    legs: Object.fromEntries(Object.entries(e.legs).map(([id, leg]) => [id, { ...leg }])),
    generator: { ...e.generator },
    strike: e.strike ? { ...e.strike } : null,
    events: [...e.events],
    hitLedger: { ...e.hitLedger },
    portalPasses: { ...e.portalPasses },
  };
}

function holdMechanism(s, snapshot, partId, callbacks) {
  // Restore the last clear physical pose and its clocks. The pilot's already
  // collision-checked movement remains real; it never counts as a portal jump.
  for (const key of Object.keys(s.cathedral)) delete s.cathedral[key];
  Object.assign(s.cathedral, snapshot, {
    pilot: position(s),
    previousPilot: position(s),
    motionHeld: true,
  });
  s.motionHold = { partId, reason: 'moving-solid-clearance' };
  s.shots = [];
  hurt(s, 'collision', callbacks);
}

function tick(s, input, dt, callbacks, worldSteering) {
  s.grace = Math.max(0, s.grace - dt);
  s.pulseTime = Math.max(0, (s.pulseTime ?? 0) - dt);
  stepSkyOverdrive(s, dt);
  s.bursts = s.bursts.filter((burst) => (burst.life -= dt) > 0);
  const snapshot = mechanismSnapshot(s.cathedral);
  const before = position(s),
    previousPose = s.cathedral.pose,
    prior = s.cathedral.phase;
  if (skyCathedralSystems(s.cathedral).controls && s.lives > 0)
    move(s, input, dt, callbacks, worldSteering);
  stepSkyCathedral(s.cathedral, dt, {
    pilot: position(s),
    pilotAlive: s.lives > 0,
    activeProjectileIds: s.shots.map((shot) => shot.id),
  });
  const terminal = ['pilot-down', 'lost'].includes(s.cathedral.phase);
  const moving = terminal
    ? null
    : skyCathedralMovingSweep(
        before,
        position(s),
        previousPose,
        s.cathedral.pose,
        SPACE.pilotRadius,
      );
  if (moving?.invalid || !s.cathedral.pose.valid) {
    s.geometryError = moving?.reason ?? s.cathedral.pose.failures.join(',');
    s.status = 'geometry-error';
    return;
  }
  if (moving) {
    const resolved = skyCathedralResolvePilot(
      position(s),
      s.cathedral.pose,
      SPACE.pilotRadius,
      Math.min(0.75, Math.max(0.02, moving.motionMargin ?? 0.02)),
    );
    if (
      resolved.blocked ||
      (!skyCathedralSystems(s.cathedral).controls && resolved.distance > 1e-6)
    ) {
      holdMechanism(s, snapshot, moving.partId, callbacks);
    } else if (resolved.distance > 0) {
      s.x = resolved.point.x;
      s.y = resolved.point.y;
      s.pilotZ = resolved.point.z;
      // This is bounded physical separation, not user steering or a portal
      // crossing. Do not let the next tick turn the correction into progress.
      s.cathedral.pilot = copy(resolved.point);
      s.cathedral.previousPilot = copy(resolved.point);
      hurt(s, 'collision', callbacks);
    }
  }
  if (!s.cathedral.motionHeld || s.cathedral.age > snapshot.age) {
    s.cathedral.motionHeld = false;
    s.motionHold = null;
  }
  refresh(s);
  if (
    input.pulse &&
    s.pulse &&
    skyCathedralSystems(s.cathedral).controls &&
    !s.cathedral.motionHeld
  ) {
    s.pulse = 0;
    s.pulseTime = 0.65;
    s.grace = Math.max(s.grace, 1);
    s.threats = [];
  }
  if (!s.cathedral.motionHeld) hazards(s, before, callbacks);
  if (callbacks && s.status !== 'playing') {
    clearSkyBonuses(s);
    failSkyCathedralPilot(s.cathedral);
    refresh(s);
    return;
  }
  if (prior !== s.cathedral.phase) s.shots = [];
  const controls =
    skyCathedralSystems(s.cathedral).controls && s.lives > 0 && !s.cathedral.motionHeld;
  s.aimPoint = skyValidatedAimPoint(s, input.aimPoint);
  stepSkyBonuses(s, dt);
  if (skyFlightCapabilities(s).weapons && s.aimPoint && input.fire !== false)
    emitSkyShots(s, skyEffectiveEquipment(s), dt, input.weaponMuzzles);
  if (controls)
    stepSkyProjectiles(s, dt, {
      destroy: () => {},
      burst:
        callbacks?.burst ??
        ((_state, point, color) => s.bursts.push({ ...point, color, life: 0.4 })),
      resolveContact: (_state, target, shot, point, impact) =>
        target.componentId
          ? applySkyCathedralContact(s.cathedral, {
              projectileId: shot.id,
              componentId: target.componentId,
              point,
              damage: impact.damage,
            })
          : { handled: true, damage: 0, quality: 'armor' },
    });
  else s.shots = [];
  s.shots = s.shots.filter(skyProjectileAlive);
  refresh(s);
  settle(s, callbacks);
}

/** Dormant campaign adapter. Caller owns fixed-step run clocks, damage and
 * stage settlement. Only normalized local x/y/forward movement is accepted;
 * world target coordinates belong to the standalone diagnostic wrapper. */
export function stepSkyCathedralFlight(s, input, dt, callbacks) {
  if (
    s?.status !== 'playing' ||
    !input ||
    !Number.isFinite(dt) ||
    dt <= 0 ||
    dt > 0.1 ||
    !callbacks?.hit ||
    !callbacks?.clearStage
  )
    return s;
  if (!startSkyCathedralFlight(s)) return s;
  const count = Math.ceil(dt * 120 - 1e-9);
  for (let i = 0; i < count && s.status === 'playing'; i++)
    tick(s, input, dt / count, callbacks, false);
  return s;
}

/** Speed-bounded fixed-step gameplay. Inputs contain world axis values or
 * finite target coordinates, plus a real world aim point/fire flag. Large or
 * malformed dt is ignored; accepted dt is subdivided to at most1/120s. Paused
 * and terminal calls are inert. No input can directly set HP, phase or lock. */
export function stepSkyCathedralRun(s, input = {}, dt = 1 / 120) {
  if (!s || !input || typeof input !== 'object' || !Number.isFinite(dt) || dt <= 0 || dt > 0.1)
    return s;
  if (s.status === 'ready' && input.start === true) s.status = 'playing';
  if (s.status !== 'playing') return s;
  const count = Math.ceil(dt * 120 - 1e-9);
  for (let i = 0; i < count && s.status === 'playing'; i++) {
    s.time += dt / count;
    s.tick++;
    tick(s, input, dt / count, null, true);
  }
  return s;
}
